import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../auth/AuthContext";

function EmailVerificationOverlay() {
  const { user, sendVerificationEmail, reloadUser, logout } = useAuth();
  const [resendStatus, setResendStatus] = useState(null); // null | "success" | "error"
  const [resendMessage, setResendMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    // Start polling for verification status
    pollRef.current = setInterval(async () => {
      const updated = await reloadUser();
      if (updated && updated.emailVerified) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // Refresh the app state to proceed into the application
        window.location.reload();
      }
    }, 6000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [reloadUser]);

  useEffect(() => {
    if (cooldown > 0) {
      intervalRef.current = setInterval(() => {
        setCooldown((c) => (c > 0 ? c - 1 : 0));
      }, 1000);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [cooldown]);

  const onResend = async () => {
    if (isResending || cooldown > 0) return;
    setIsResending(true);
    setResendStatus(null);
    setResendMessage("");
    try {
      const res = await sendVerificationEmail();
      if (res?.success) {
        setResendStatus("success");
        setResendMessage(res.message || "Verification email sent.");
        setCooldown(30); // 30s cooldown to prevent spamming
      } else {
        setResendStatus("error");
        setResendMessage(res?.error || "Failed to send verification email.");
      }
    } catch (e) {
      setResendStatus("error");
      setResendMessage(e?.message || "Failed to send verification email.");
    } finally {
      setIsResending(false);
    }
  };

  const onRefreshNow = async () => {
    const updated = await reloadUser();
    if (updated && updated.emailVerified) {
      window.location.reload();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        background: "rgba(0, 0, 0, 0.92)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
   >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          padding: 24,
          boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
          textAlign: "center",
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 10 }}>Verify your email</h2>
        <p style={{ opacity: 0.9, marginTop: 0 }}>
          We sent a verification link to:
        </p>
        <div
          style={{
            margin: "8px auto 16px",
            padding: "8px 12px",
            borderRadius: 8,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            display: "inline-block",
            fontWeight: 600,
          }}
        >
          {user?.email || "your email"}
        </div>

        <p style={{ opacity: 0.9, marginTop: 0 }}>
          Please click the link in the email to verify your account. This screen
          will automatically refresh once verification is complete.
        </p>

        {resendStatus && (
          <div
            style={{
              marginTop: 10,
              marginBottom: 10,
              padding: "8px 12px",
              borderRadius: 8,
              color: resendStatus === "success" ? "#22c55e" : "#ef4444",
              backgroundColor:
                resendStatus === "success"
                  ? "rgba(34,197,94,0.12)"
                  : "rgba(239,68,68,0.12)",
              border:
                resendStatus === "success"
                  ? "1px solid rgba(34,197,94,0.4)"
                  : "1px solid rgba(239,68,68,0.4)",
            }}
          >
            {resendMessage}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            alignItems: "center",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={onResend}
            disabled={isResending || cooldown > 0}
            style={{
              backgroundColor: isResending || cooldown > 0 ? "#6c757d" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: 9999,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: isResending || cooldown > 0 ? "not-allowed" : "pointer",
              minWidth: 160,
            }}
          >
            {isResending
              ? "Sending…"
              : cooldown > 0
              ? `Resend (${cooldown}s)`
              : "Resend link"}
          </button>

          <button
            onClick={onRefreshNow}
            style={{
              backgroundColor: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 9999,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
              minWidth: 160,
            }}
          >
            I verified — Refresh now
          </button>

          <button
            onClick={logout}
            style={{
              backgroundColor: "transparent",
              color: "rgba(255,255,255,0.9)",
              border: "none",
              textDecoration: "underline",
              cursor: "pointer",
              padding: "10px 8px",
              minWidth: 120,
            }}
          >
            Use a different email
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationOverlay;


